import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import type { PolicyCustomizationForm } from "@covertree/new-business-forms";
import {
  getPackageCoveragesToRender,
  prepareDataToRender,
} from "@covertree/new-business-policy-customization";

import type { EndorsementFormValues } from "@layouts/EndorsementLayout/types";

import { NO_POLICY_LEVEL_COVERAGES } from "./constants";
import { useBaseCoverages } from "./useBaseCoverages";
import { useCoverageRules } from "./useCoverageRules";
import { useRevalidateOnChange } from "./useRevalidateOnChange";
import { useSeedBaseDefaults } from "./useSeedBaseDefaults";

/** NBS "Customize your plan" on the endorsement form. */
export const usePersonalizedPlan = () => {
  const base = useBaseCoverages();
  const { getValues } = useFormContext<EndorsementFormValues>();

  const isSeeded = useSeedBaseDefaults(base);

  const coverages = useMemo(
    () =>
      base
        ? getPackageCoveragesToRender(
            prepareDataToRender(
              getValues() as unknown as PolicyCustomizationForm,
              base,
              NO_POLICY_LEVEL_COVERAGES
            )
          )
        : [],
    [base, getValues]
  );

  const keys = useMemo(
    () => coverages.map(({ validationKey }) => validationKey),
    [coverages]
  );

  const rules = useCoverageRules(keys, base);
  useRevalidateOnChange(keys);

  return { coverages: isSeeded ? coverages : [], rules };
};
